import { createFileRoute, Link } from "@tanstack/react-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { listCategories, listPublishedArticles } from "@/lib/blog.functions";
import { ArticleCard } from "@/components/blog/ArticleCard";
import { BlogSidebar } from "@/components/blog/BlogSidebar";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";

const SITE = "https://heallexa.com";
const BLOG_URL = `${SITE}/blog`;
const TITLE = "Heallexa Blog: Healthcare Discovery Guides";
const DESCRIPTION =
 "Patient and provider guides on finding healthcare providers by specialty, location, insurance, and condition.";

export const Route = createFileRoute("/blog/")({
 head: () => ({
 meta: [
 { title: TITLE },
 { name: "description", content: DESCRIPTION },
 { property: "og:title", content: TITLE },
 { property: "og:description", content: DESCRIPTION },
 { property: "og:url", content: BLOG_URL },
 { property: "og:type", content: "website" },
 ],
 links: [{ rel: "canonical", href: BLOG_URL }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "Blog",
 name: TITLE,
 description: DESCRIPTION,
 url: BLOG_URL,
 inLanguage: "en-US",
 publisher: {
 "@type": "Organization",
 name: "Heallexa",
 url: SITE,
 logo: { "@type": "ImageObject", url: `${SITE}/logo.png` },
 },
 about: [
 { "@type": "Thing", name: "Healthcare Provider Discovery" },
 { "@type": "Thing", name: "Medical Specialties" },
 { "@type": "Thing", name: "Health Insurance" },
 ],
 isPartOf: { "@id": `${SITE}/#website` },
 }),
 },
 ],
 }),
 component: BlogIndex,
});

const PAGE_SIZE = 24;

function BlogIndex() {
 const [search, setSearch] = useState("");
 const { data: cats } = useQuery({ queryKey: ["blog-cats"], queryFn: () => listCategories() });
 const { data: pages, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
 queryKey: ["blog-latest-paged"],
 initialPageParam: 0,
 queryFn: ({ pageParam }) => listPublishedArticles({ data: { limit: PAGE_SIZE, offset: pageParam as number } }),
 getNextPageParam: (last, all) => (last.hasMore ? all.length * PAGE_SIZE : undefined),
 });

 const catMap = useMemo(() => {
 const m = new Map<string, string>();
 (cats ?? []).forEach((c: any) => m.set(c.id, c.name));
 return m;
 }, [cats]);

 const rows = useMemo(() => {
 const all = (pages?.pages ?? []).flatMap((p: any) => p.rows ?? []);
 return all.map((a: any) => ({ ...a, category_name: catMap.get(a.category_id) ?? null }));
 }, [pages, catMap]);

 const filtered = useMemo(() => {
 const q = search.trim().toLowerCase();
 if (!q) return rows;
 return rows.filter((a: any) =>
 a.title?.toLowerCase().includes(q) ||
 a.excerpt?.toLowerCase().includes(q) ||
 a.category_name?.toLowerCase().includes(q),
 );
 }, [rows, search]);

 const featured = filtered[0];
 const rest = filtered.slice(1);

 return (
 <div className="min-h-screen bg-background">
 <Navbar />

 {/* Hero */}
 <header className="relative pt-28 pb-14 overflow-hidden border-b border-border">
 <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-accent/10 to-transparent" />
 <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
 <div className="absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-accent/20 blur-3xl" />
 <div className="relative mx-auto max-w-6xl px-6">
 <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full bg-primary/10 text-primary">
 <Sparkles className="h-3 w-3" /> Heallexa Blog
 </span>
 <h1 className="text-4xl md:text-6xl font-semibold tracking-tight mt-4 max-w-3xl">
 Healthcare discovery, <span className="bg-gradient-to-r from-primary to-accent-foreground bg-clip-text text-transparent">made simple</span>
 </h1>
 <p className="text-muted-foreground mt-4 max-w-2xl text-lg">
 Find the right provider, confirm insurance, understand specialties, and learn how to book care. Informational only, not medical advice.
 </p>
 </div>
 </header>

 <main className="mx-auto max-w-6xl px-6 py-12 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
 <BlogSidebar categories={cats ?? []} search={search} onSearchChange={setSearch} />

 <div className="space-y-10 min-w-0">
 <section>
 <div className="flex items-end justify-between mb-5">
 <h2 className="text-2xl font-semibold tracking-tight">
 {search ? `Results for "${search}"` : "Latest articles"}
 </h2>
 <span className="text-xs text-muted-foreground">{filtered.length} article{filtered.length === 1 ? "" : "s"}</span>
 </div>

 {filtered.length === 0 ? (
 <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center">
 <p className="text-sm text-muted-foreground">
 {search ? "No articles match your search." : "No published articles yet. Check back soon."}
 </p>
 </div>
 ) : (
 <>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
 {featured && <ArticleCard article={featured} featured />}
 {rest.map((a: any) => (
 <ArticleCard key={a.id} article={a} />
 ))}
 </div>
 {!search && hasNextPage && (
 <div className="flex justify-center mt-8">
 <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
 {isFetchingNextPage ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading\u2026</> : "Load more"}
 </Button>
 </div>
 )}
 </>
 )}
 </section>

 {!search && (cats ?? []).length > 0 && (
 <section>
 <h2 className="text-2xl font-semibold tracking-tight mb-5">Browse by category</h2>
 <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
 {(cats ?? []).map((c: any, i: number) => {
 const gradients = [
 "from-primary/20 to-accent/10",
 "from-emerald-500/15 to-sky-500/10",
 "from-amber-500/15 to-rose-500/10",
 "from-violet-500/15 to-primary/10",
 "from-sky-500/15 to-accent/10",
 "from-rose-500/15 to-amber-500/10",
 ];
 const g = gradients[i % gradients.length];
 return (
 <Link
 key={c.id}
 to="/blog/$category"
 params={{ category: c.slug }}
 className={`group relative overflow-hidden rounded-xl border border-border p-5 bg-gradient-to-br ${g} hover:-translate-y-0.5 hover:shadow-md transition-all`}
 >
 <p className="font-semibold text-foreground">{c.name}</p>
 {c.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.description}</p>}
 <span className="mt-3 inline-block text-xs font-medium text-primary group-hover:underline">Explore \u2192</span>
 </Link>
 );
 })}
 </div>
 </section>
 )}
 </div>
 </main>

 <FooterSection />
 </div>
 );
}
